import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom-v5-compat";

import Icon from "Atoms/Icons";

import {
  Price,
  ResponsiveTitleBar,
  ResponsivePrimaryAction,
  Button,
  useSubmitButton,
  ErrorBox,
} from "../../Atoms";
import { createTabCheckoutSession } from "../Cart/requests";
import { useTimeoutMessage } from "../Checkout/useTimeoutSearchParam";
import { useLoadedCustomer } from "../Customer/queries";
import { useLoadedCustomerTab } from "../CustomerTab/queries";
import { getMaxPaymentAmountFor } from "../CustomerTab/selectors";
import { useCreateOrderItemClaim } from "./OrderItemClaims/queries";
import { CustomerPillList, Pill } from "./Pill";
import { useLoadedBillSplit } from "./queries";
import {
  getClaimedOrderItemsIn,
  getCustomerTabBalance,
  getOrderItemsTotal,
  getOthersClaimedUnpaidOrderItems,
  getOthersClaimedValue,
  getPaidAtTillAmount,
  getTotalPaid,
  getTotalRemainingUnclaimed,
} from "./selectors";
import { getUnclaimedOrderItems } from "./selectors";
import { getTotalUnassignedTransactionsFor } from "./selectors";

const LineItem = ({ label, value }: { label: string; value: number }) => {
  return (
    <div className="flex justify-between text-lg font-semibold">
      <div>{label}</div>
      <Price showCurrency value={value} />
    </div>
  );
};
const OwnerReview = () => {
  const { t } = useTranslation();
  const { data: customerTab } = useLoadedCustomerTab();
  const { data: customer } = useLoadedCustomer();
  const { data: billSplit } = useLoadedBillSplit();

  const { errorMessage, setErrorMessage } = useTimeoutMessage();

  const getClaimedOrderItemsFor = getClaimedOrderItemsIn(
    billSplit,
    customerTab,
  );
  const yourClaimedOrderItems = getClaimedOrderItemsFor(customer);
  const yourClaimedOrderItemsTotal = getOrderItemsTotal(yourClaimedOrderItems);

  const paidAtTillAmount = getTotalPaid(customerTab);
  const customerTabBalance = getCustomerTabBalance(customerTab);
  const othersClaimedValue = getOthersClaimedUnpaidOrderItems(
    billSplit,
    customerTab,
    customer,
  );
  const isClaimingRemainder = billSplit.remainderClaimerId === customer.uuid;
  const totalRemaining = getTotalRemainingUnclaimed(billSplit, customerTab);
  const unclaimedTotal = isClaimingRemainder ? 0 : totalRemaining;
  const yourClaimedTotal =
    yourClaimedOrderItemsTotal + (isClaimingRemainder ? totalRemaining : 0);

  const [submissionState, trackSubmit] = useSubmitButton();
  const awaitingPayment = billSplit.customerSessions.every(
    (cs) => !!cs.completedAt || cs.customerId === customer.uuid,
  );
  const startBillSplitCheckoutSession = async () => {
    try {
      const checkoutUrl = await createTabCheckoutSession(customerTab.uuid, {
        billSplitCustomerSessionId: billSplit.customerSessions.find(
          (cs) => cs.customerId === customer.uuid,
        )?.uuid,
      });
      window.location.assign(checkoutUrl);
      await new Promise((r) => setTimeout(r, 3000));
    } catch (e) {
      console.error(e.body);
      setErrorMessage(e.body.error);
    }
  };
  return (
    <div className="mx-auto max-w-screen-sm space-y-32">
      <ResponsiveTitleBar backTo={"../review"}>
        {t("billSplit.finishPaying")}{" "}
      </ResponsiveTitleBar>
      <div className="space-y-12">
        <div>
          <h3 className="text-2xl font-bold">
            {!awaitingPayment
              ? t("billSplitOwnerReview.waitingOnYourFriends")
              : t("billSplitOwnerReview.everyoneElseHasPaid")}
          </h3>
          <div>
            {!awaitingPayment
              ? t("billSplitOwnerReview.waitingOnYourFriendsDescription")
              : t("billSplitOwnerReview.everyoneElseHasPaidDescription")}
          </div>
        </div>
        <CustomerPillList
          billSplit={billSplit}
          customerTab={customerTab}
          currentCustomer={customer}
        />
      </div>
      {errorMessage && (
        <div className="mx-auto max-w-screen-sm">
          <ErrorBox>
            {t([
              `billSplitError.serverErrors.${errorMessage}`,
              "billSplitError.serverErrors.fallback",
            ])}
          </ErrorBox>
        </div>
      )}
      <div className="space-y-16">
        <LineItem
          label={t("billSplitOwnerReview.alreadyPaid")}
          value={paidAtTillAmount}
        />
        <div className="h-32" />
        <div />
        <h2 className="text-2xl font-bold">
          {t("billSplitOwnerReview.whatYouAreCurrentlyPayingFor")}
          <div className="text-base font-normal">
            {t("billSplitOwnerReview.whatYouAreCurrentlyPayingForDescription")}
          </div>
        </h2>
        <LineItem
          label={t("billSplitOwnerReview.othersClaimedButNotYetPaid")}
          value={othersClaimedValue}
        />
        <LineItem
          label={t("billSplitOwnerReview.yourClaimedValue")}
          value={yourClaimedTotal}
        />
        <LineItem
          label={t("billSplitOwnerReview.unclaimedValue")}
          value={unclaimedTotal}
        />
        {/* <h3 className="font-bold text-xl">
          {t("billSplit.totalOutstandingBalance")}{" "}
          <Price showCurrency value={paidAtTillAmount} />
        </h3> */}
      </div>

      <div className="flex">
        <Link to="..">
          <Pill>
            <Icon icon="search" color="venue-primary" />
            {t("billSplit.reviewBill")}
          </Pill>
        </Link>
      </div>
      {!awaitingPayment && (
        <Button
          secondary
          submissionState={submissionState}
          submissionOptions={{
            loadingMessage: t("billSplit.creatingCheckoutSession"),
          }}
          onClick={trackSubmit(startBillSplitCheckoutSession)}
        >
          {t("billSplitOwnerReview.payAllNow")}
          <Price showCurrency value={customerTabBalance} />
        </Button>
      )}

      <ResponsivePrimaryAction>
        {!awaitingPayment && true ? (
          <div className="block w-full items-center justify-center p-12 text-center text-lg font-bold">
            {t("billSplitOwnerReview.waitingForFriends")}
          </div>
        ) : customerTabBalance > 0 ? (
          <Button
            primary
            submissionState={submissionState}
            submissionOptions={{
              loadingMessage: t("billSplit.creatingCheckoutSession"),
            }}
            onClick={trackSubmit(startBillSplitCheckoutSession)}
          >
            {t("billSplit.payNow")}
            <Price showCurrency value={customerTabBalance} />
          </Button>
        ) : (
          <div className="p-12 text-center text-lg font-bold">
            {t("billSplit.nothingToPay")}
          </div>
        )}
      </ResponsivePrimaryAction>
    </div>
  );
};

export default OwnerReview;
